/**
 * ClientReportV3 — Editorial client report rendered in React.
 * Inherits the CoinBills brand DNA: monochrome paper / cream / black with a
 * single yellow accent + asterisk. Editorial serif (Instrument Serif) + Inter.
 *
 * NOT a dashboard. A composed, paced experience: cover → snapshot →
 * capital story → performance intelligence → strategic commentary → footer.
 *
 * Data contract: GET /api/clients/{id}/report-v3 (IA-locked).
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { axiosInstance } from '../App';
import './ClientReportV3.css';

const fmtMoney = (sym, n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (sym === '₹') {
    if (abs >= 1e7) return `${sym}${(n / 1e7).toFixed(2)} Cr`;
    if (abs >= 1e5) return `${sym}${(n / 1e5).toFixed(2)} L`;
    return `${sym}${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
  // USDT and other base currencies: en-US grouping (120,050 not 1,20,050).
  return `${sym}${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const HUMANISE_METHOD = {
  cash: 'cash',
  cash_converted: 'cash converted',
  currency_conversion: 'currency conversion',
  usdt_exchange: 'via USDT exchange',
  reinvestment: 'reinvested',
};
const humaniseMethod = (m) => {
  if (!m) return '';
  const k = String(m).toLowerCase();
  return HUMANISE_METHOD[k] || k.replace(/_/g, ' ');
};

const fmtPct = (n, sign = false) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const s = `${sign && n > 0 ? '+' : ''}${Number(n).toFixed(2)}%`;
  return s;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const dateRange = (from, to) => `${fmtDate(from)} — ${fmtDate(to)}`;

// Asterisk used as brand mark in lieu of a logo bitmap.
const Mark = () => <span className="v3-asterisk">*</span>;

// CoinBills C-monogram (extracted from the brand identity book, page 4).
// Inverted via CSS filter for use on dark / ink sections.
const CMark = ({ inverted = false, size = 22 }) => (
  <img
    src="/coinbills-c-mark.png"
    alt="CoinBills"
    className={`v3-cmark${inverted ? ' is-inverted' : ''}`}
    style={{ height: size, width: 'auto' }}
  />
);

// =============================================================================
// PAGE 1 — COVER
// =============================================================================
function Cover({ cover }) {
  const period = cover?.reporting_period || {};
  return (
    <section className="v3-section v3-section--ink" data-testid="report-v3-cover">
      <div className="v3-cover-grid">
        <header className="v3-cover-top">
          <div className="v3-cover-lockup">
            <CMark inverted size={36} />
            <div className="v3-cover-wordmark">
              coinbills<Mark />
            </div>
          </div>
          <div className="v3-eyebrow">Client Report · v3.0</div>
        </header>

        <div className="v3-cover-center">
          <div className="v3-eyebrow">A Statement of Capital</div>
          <h1 className="v3-display">
            <em>{cover?.client_name || '—'}</em>
            <br />
            Portfolio.
          </h1>
          <p className="v3-body" style={{ maxWidth: '52ch' }}>
            {cover?.branding?.tagline || 'Capital Stewardship & Performance Intelligence.'}
            {' '}
            A reporting period of measured capital and quiet returns.
          </p>
        </div>

        <div className="v3-cover-bottom">
          <div>
            <span className="v3-eyebrow">Portfolio</span>
            <span className="value">{cover?.portfolio_id || '—'}</span>
          </div>
          <div>
            <span className="v3-eyebrow">Period</span>
            <span className="value">{dateRange(period.from, period.to)}</span>
          </div>
          <div>
            <span className="v3-eyebrow">Issued</span>
            <span className="value">{fmtDate(cover?.generated_at)}</span>
          </div>
          <div>
            <span className="v3-eyebrow">Version</span>
            <span className="value">v3.0 · Confidential</span>
          </div>
        </div>
      </div>
      {/* Quiet watermark — half-bleed C-monogram in the bottom-right of the cover */}
      <span className="v3-cover-watermark" aria-hidden="true">
        <CMark inverted size={420} />
      </span>
    </section>
  );
}

// =============================================================================
// PAGE 2 — EXECUTIVE SNAPSHOT
// =============================================================================
function Snapshot({ snapshot }) {
  const sym = snapshot?.base_symbol || '$';
  const twac = snapshot?.twac_roi_pct;
  const sign = twac != null && twac >= 0 ? '+' : '';
  return (
    <section className="v3-section" data-testid="report-v3-snapshot">
      <div className="v3-eyebrow">02 · Executive Snapshot</div>
      <h2 className="v3-h2" style={{ marginTop: 24, maxWidth: '18ch' }}>
        Time-weighted return,<br />
        <em>distilled.</em>
      </h2>

      <div className="v3-snap-grid" style={{ marginTop: 64 }}>
        <div className="v3-snap-hero">
          <div>
            <div className="v3-eyebrow" style={{ marginBottom: 12 }}>TWAC ROI · primary metric</div>
            <div className="v3-snap-hero-num">
              <span className="sign">{sign}</span>
              <span>{twac != null ? Number(twac).toFixed(2) : '—'}</span>
              <span className="pct">%</span>
            </div>
          </div>
          <div className="v3-snap-hero-label">
            <span className="v3-eyebrow">Profit per capital-day deployed</span>
            <span className="name">
              Capital that worked, measured against the time it was deployed —
              not the time it was idle.
            </span>
          </div>
        </div>

        <div className="v3-snap-side">
          <div className="v3-snap-cell">
            <span className="label">Total Invested</span>
            <span className="num">{fmtMoney(sym, snapshot?.total_invested)}</span>
            {snapshot?.total_reinvested > 0 && (
              <span className="denom" data-testid="snap-incl-reinvested">
                incl. {fmtMoney(sym, snapshot.total_reinvested)} reinvested
              </span>
            )}
            <span className="denom">capital deployed across {snapshot?.portfolio_duration_human || '—'}</span>
          </div>
          <div className="v3-snap-cell">
            <span className="label">Current NAV</span>
            <span className="num">{fmtMoney(sym, snapshot?.current_nav)}</span>
            <span className="denom">net of withdrawals</span>
          </div>
          <div className="v3-snap-cell">
            <span className="label">Total Profit</span>
            <span className="num">{fmtMoney(sym, snapshot?.total_profit)}</span>
            <span className="denom">realized · distributed {fmtMoney(sym, snapshot?.profit_distributed)}</span>
          </div>
          <div className="v3-snap-cell">
            <span className="label">Cumulative ROI</span>
            <span className="num">{fmtPct(snapshot?.cumulative_roi_pct)}</span>
            <span className="denom">profit ÷ total invested</span>
          </div>
          <div className="v3-snap-cell">
            <span className="label">XIRR</span>
            <span className="num">{fmtPct(snapshot?.xirr_pct)}</span>
            <span className="denom">annualised, dated cashflows</span>
          </div>
          <div className="v3-snap-cell">
            <span className="label">Duration</span>
            <span className="num" style={{ fontSize: '32px' }}>{snapshot?.portfolio_duration_human || '—'}</span>
            <span className="denom">{snapshot?.portfolio_duration_days || 0} days held</span>
          </div>
        </div>
      </div>

      {snapshot?.summary_sentence && (
        <p className="v3-snap-summary">
          <Mark /> {snapshot.summary_sentence}
        </p>
      )}

      {snapshot?.payout_currency_summary && (
        <p className="v3-snap-footnote" data-testid="report-v3-payout-fx-note">
          Of {fmtMoney(sym, snapshot.profit_distributed)} distributed,{' '}
          {snapshot.payout_currency_summary.native_symbol}
          {Number(snapshot.payout_currency_summary.native_total).toLocaleString(
            snapshot.payout_currency_summary.native_currency === 'INR' ? 'en-IN' : 'en-US',
            { maximumFractionDigits: 0 }
          )}{' '}
          was paid in {snapshot.payout_currency_summary.native_currency}, valued
          at {fmtMoney(sym, snapshot.payout_currency_summary.base_total)} using
          an average effective rate of{' '}
          <strong>
            {Number(snapshot.payout_currency_summary.avg_effective_rate).toFixed(2)}
          </strong>{' '}
          {snapshot.payout_currency_summary.rate_direction}.
        </p>
      )}
    </section>
  );
}

// =============================================================================
// PAGE 3 — CAPITAL JOURNEY
// =============================================================================
const CATEGORY_LABELS = {
  Deposit: 'Deposit',
  Reinvestment: 'Reinvestment',
  Payout: 'Payout',
  Withdrawal: 'Withdrawal',
  'Operational Conversion': 'Operational Conversion',
};

function CapitalJourney({ capital_journey, sym }) {
  const totals = capital_journey?.totals || {};
  const events = capital_journey?.events || [];
  return (
    <section className="v3-section v3-section--bone" data-testid="report-v3-journey">
      <div className="v3-eyebrow">03 · Capital Journey</div>
      <h2 className="v3-h2" style={{ marginTop: 24, maxWidth: '20ch' }}>
        Movement <em>of money,</em><br />
        not noise.
      </h2>
      <p className="v3-body" style={{ marginTop: 24 }}>
        Every deposit, payout, reinvestment and operational conversion — in order.
        When INR is realised as USDT (or vice versa), the capital is counted at
        the realised rate, dated back to when it actually arrived.
      </p>

      <div className="v3-journey-totals">
        <div className="v3-journey-total">
          <span className="label">Deposits</span>
          <span className="value">{fmtMoney(sym, totals.deposits)}</span>
        </div>
        <div className="v3-journey-total">
          <span className="label">Reinvestments</span>
          <span className="value">{fmtMoney(sym, totals.reinvestments)}</span>
        </div>
        <div className="v3-journey-total">
          <span className="label">Payouts</span>
          <span className="value">{fmtMoney(sym, totals.payouts)}</span>
        </div>
        <div className="v3-journey-total">
          <span className="label">Withdrawals</span>
          <span className="value">{fmtMoney(sym, totals.withdrawals)}</span>
        </div>
        {totals.conversion_native_total > 0 && totals.conversion_native_currency && (
          <div className="v3-journey-total" data-testid="report-v3-converted-tile">
            <span className="label">
              Converted ({totals.conversion_native_currency})
            </span>
            <span className="value">
              {totals.conversion_native_currency === 'INR' ? '₹' : '$'}
              {Number(totals.conversion_native_total).toLocaleString(
                totals.conversion_native_currency === 'INR' ? 'en-IN' : 'en-US',
                { maximumFractionDigits: 0 }
              )}
            </span>
          </div>
        )}
      </div>

      <div className="v3-journey-list">
        {events.length === 0 && (
          <p className="v3-body" style={{ marginTop: 16 }}>
            No movement recorded for this period.
          </p>
        )}
        {events.map((e, i) => {
          const isConv = e.category === 'Operational Conversion';
          const isPayout = e.category === 'Payout';
          const baseSym = sym;
          const rawSym = e.raw_currency === 'INR' ? '₹' : e.raw_currency === 'USDT' ? '$' : '';
          const crossCurrency = e.raw_currency && e.raw_currency !== baseSym && e.raw_amount;
          // For cross-currency payouts, headline the native cash leg and
          // parenthesise the base equivalent: "$479.38 USDT (~₹46,500 equivalent)".
          // For all other rows, keep the legacy two-column layout.
          const showCrossCcyPayout = isPayout && crossCurrency;
          const showRawCol = crossCurrency && !showCrossCcyPayout;
          const fmtRaw = (cur, amt, frac = 0) =>
            `${rawSym}${Number(amt).toLocaleString(cur === 'INR' ? 'en-IN' : 'en-US', { maximumFractionDigits: frac, minimumFractionDigits: frac })}`;
          return (
            <div
              key={i}
              className={`v3-journey-row ${isConv ? 'is-conversion' : ''}`}
              data-testid={`journey-row-${i}`}
            >
              <span className="date">{fmtDate(e.date)}</span>
              <span className="cat">{CATEGORY_LABELS[e.category] || e.category}</span>
              <span className="raw" data-testid={`journey-row-${i}-raw`}>
                {showCrossCcyPayout
                  ? `${fmtRaw(e.raw_currency, e.raw_amount, 2)} ${e.raw_currency}`
                  : showRawCol
                    ? `${fmtRaw(e.raw_currency, e.raw_amount, 0)} ${e.raw_currency}${e.note ? ` · ${humaniseMethod(e.note)}` : ''}`
                    : (humaniseMethod(e.note) || '')}
              </span>
              <span className="amt" data-testid={`journey-row-${i}-amt`}>
                {showCrossCcyPayout
                  ? `(~${fmtMoney(baseSym, e.amount_base)} equivalent)`
                  : fmtMoney(baseSym, e.amount_base)}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// =============================================================================
// PAGE 4 — PERFORMANCE INTELLIGENCE
// =============================================================================
function Performance({ performance, snapshot, calendarYearRoi }) {
  const cycles = performance?.cycles || [];
  const max = Math.max(1, ...cycles.map((c) => Math.max(0, Number(c.roi_pct) || 0)));
  const best = performance?.best_cycle;
  const worst = performance?.worst_cycle;
  const sym = snapshot?.base_symbol || '$';
  const baseCurr = snapshot?.base_currency || 'USDT';
  const fmtBase = (n) =>
    n == null
      ? '—'
      : `${sym}${Number(n).toLocaleString(baseCurr === 'INR' ? 'en-IN' : 'en-US', { maximumFractionDigits: 0 })}`;

  return (
    <section className="v3-section" data-testid="report-v3-performance">
      <div className="v3-eyebrow">04 · Performance Intelligence</div>
      <h2 className="v3-h2" style={{ marginTop: 24, maxWidth: '20ch' }}>
        Quality. <em>Consistency.</em><br />
        Risk-adjusted clarity.
      </h2>

      <div className="v3-perf-grid">
        <div className="v3-perf-cell">
          <span className="label">Avg Cycle ROI</span>
          <span className="num">{fmtPct(performance?.avg_cycle_roi_pct)}</span>
        </div>
        <div className="v3-perf-cell">
          <span className="label">Sharpe Ratio</span>
          <span className="num">{performance?.sharpe_ratio != null ? Number(performance.sharpe_ratio).toFixed(2) : '—'}</span>
        </div>
        <div className="v3-perf-cell">
          <span className="label">Win Rate</span>
          <span className="num">{fmtPct(performance?.win_rate_pct)}</span>
        </div>
        <div className="v3-perf-cell">
          <span className="label">Volatility</span>
          <span className="num">{fmtPct(performance?.volatility_pct)}</span>
        </div>
        <div className="v3-perf-cell">
          <span className="label">Cycles Recorded</span>
          <span className="num">{cycles.length}</span>
        </div>
        <div className="v3-perf-cell">
          <span className="label">Best · Worst Δ</span>
          <span className="num">
            {best && worst ? fmtPct(best.roi_pct - worst.roi_pct) : '—'}
          </span>
        </div>
        <div className="v3-perf-cell" data-testid="perf-max-drawdown">
          <span className="label">Max Drawdown</span>
          <span className="num">{fmtPct(snapshot?.max_drawdown_pct)}</span>
        </div>
        <div className="v3-perf-cell" data-testid="perf-win-streak">
          <span className="label">Win Streak</span>
          <span className="num">
            {snapshot?.longest_win_streak != null ? Number(snapshot.longest_win_streak).toFixed(0) : '—'}
          </span>
        </div>
        <div className="v3-perf-cell" data-testid="perf-twr">
          <span className="label">TWR</span>
          <span className="num">{fmtPct(snapshot?.twr_pct)}</span>
        </div>
      </div>

      <div className="v3-perf-chart" data-testid="perf-chart">
        <div className="v3-eyebrow">Per-cycle return</div>
        <div className="v3-perf-chart-bars">
          {cycles.length === 0 && (
            <span className="v3-meta" style={{ alignSelf: 'center' }}>
              <em>No closed cycles in this reporting period.</em>
            </span>
          )}
          {cycles.map((c, i) => {
            const h = Math.max(2, ((Number(c.roi_pct) || 0) / max) * 100);
            const isBest = best && c.name === best.name && c.roi_pct === best.roi_pct;
            const isWorst = worst && c.name === worst.name && c.roi_pct === worst.roi_pct;
            return (
              <div
                key={i}
                className={`v3-perf-bar ${isBest ? 'is-best' : ''} ${isWorst ? 'is-worst' : ''}`}
                style={{ height: `${h}%` }}
                title={`${c.name}: ${c.roi_pct}%`}
              />
            );
          })}
        </div>
        {cycles.length > 0 && (
          <div className="v3-perf-chart-axis">
            <span>{cycles[0]?.name || ''}</span>
            <span>{cycles[cycles.length - 1]?.name || ''}</span>
          </div>
        )}
        {performance?.total_cycle_count > cycles.length && (
          <p className="v3-perf-chart-clip-note" data-testid="perf-chart-clip-note">
            Showing last {cycles.length} of {performance.total_cycle_count} closed cycles · full history in appendix.
          </p>
        )}
      </div>

      {(best || worst) && (
        <div className="v3-perf-bestworst">
          <div>
            <span className="lbl">Best Cycle</span>
            <span className="v">
              {best ? `${best.name} · ${fmtPct(best.roi_pct, true)}` : '—'}
            </span>
          </div>
          <div>
            <span className="lbl">Worst Cycle</span>
            <span className="v">
              {worst ? `${worst.name} · ${fmtPct(worst.roi_pct, true)}` : '—'}
            </span>
          </div>
        </div>
      )}

      {Array.isArray(calendarYearRoi) && calendarYearRoi.length > 0 && (
        <div className="v3-cal-year" data-testid="report-v3-calendar-year-roi">
          <div className="v3-eyebrow" style={{ marginTop: 56 }}>Calendar-Year ROI</div>
          <table className="v3-cal-year-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Profit</th>
                <th>Capital Basis</th>
                <th>Basis Type</th>
                <th>ROI%</th>
              </tr>
            </thead>
            <tbody>
              {calendarYearRoi.map((row) => (
                <tr key={row.year}>
                  <td>
                    {row.year}
                    {row.is_partial && <span className="ytd-tag"> (YTD)</span>}
                  </td>
                  <td>{fmtBase(row.profit)}</td>
                  <td>{fmtBase(row.basis)}</td>
                  <td>{row.basis_label === 'avg' ? 'Avg deployed' : 'Year-start'}</td>
                  <td>{row.roi_pct != null ? `${Number(row.roi_pct).toFixed(2)}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// =============================================================================
// PAGE 5 — AI INSIGHTS
// =============================================================================
function AIInsights({ ai_insights }) {
  const insights = ai_insights?.insights || [];
  return (
    <section className="v3-section v3-section--ink" data-testid="report-v3-ai">
      <div className="v3-eyebrow">05 · Strategic Commentary</div>
      <h2 className="v3-ai-headline" style={{ marginTop: 32 }}>
        <em>{ai_insights?.headline || 'Portfolio review.'}</em>
      </h2>

      <div className="v3-ai-list">
        {insights.map((ins, i) => (
          <div key={i} className="v3-ai-stanza" data-testid={`ai-insight-${i}`}>
            <span className="num">{String(i + 1).padStart(2, '0')}</span>
            <div className="body">
              <div className="v3-ai-line">
                <span className="tag">Observation</span>
                <span className="txt">{ins.observation}</span>
              </div>
              <div className="v3-ai-line">
                <span className="tag">Interpretation</span>
                <span className="txt">{ins.interpretation}</span>
              </div>
              <div className="v3-ai-line is-action">
                <span className="tag">Suggested Action</span>
                <span className="txt">{ins.suggested_action}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="v3-meta" style={{ marginTop: 48, color: 'rgba(246,243,234,0.4)' }}>
        Generated {fmtDate(ai_insights?.generated_at)} · {ai_insights?.model || 'fallback'}
      </div>
    </section>
  );
}

// =============================================================================
// FOOTER
// =============================================================================
function ReportFooter({ footer }) {
  return (
    <footer className="v3-footer" data-testid="report-v3-footer">
      <div className="formulas">
        <div className="v3-eyebrow" style={{ marginBottom: 8 }}>Notes on Calculation</div>
        {(footer?.formulas || []).map((f, i) => (
          <div key={i} className="formula">
            <span className="name">{f.name}</span>
            <span className="desc">{f.formula}</span>
          </div>
        ))}
      </div>
      <div>
        <div className="v3-eyebrow" style={{ marginBottom: 8 }}>Disclaimer</div>
        <p className="disclaimer">{footer?.disclaimer || ''}</p>
        <div className="v3-footer-mark">
          <CMark size={20} />
          <span className="version">
            coinbills<Mark /> · client report {footer?.report_version || 'v3.0'} · confidential
          </span>
        </div>
      </div>
    </footer>
  );
}

// =============================================================================
// PAGE
// =============================================================================
export default function ClientReportV3() {
  const { clientId } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get(
        `/clients/${clientId}/report-v3`,
        { signal, timeout: 45000 }
      );
      setReport(data);
    } catch (e) {
      if (axios.isCancel?.(e) || e?.name === 'CanceledError') return;
      setError(e?.response?.data?.detail || e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
    /* eslint-disable-next-line */
  }, [clientId]);

  if (loading) {
    return (
      <div className="v3-root">
        <div className="v3-loader" data-testid="report-v3-loading">
          <Mark />&nbsp; Composing report…
        </div>
      </div>
    );
  }
  if (error || !report) {
    return (
      <div className="v3-root">
        <div className="v3-loader" data-testid="report-v3-error">
          {error || 'Report unavailable.'}
        </div>
      </div>
    );
  }

  const sym = report.snapshot?.base_symbol || '$';

  return (
    <div className="v3-root" data-testid="client-report-v3">
      <div className="v3-utility">
        <div className="v3-utility-left">
          <CMark size={18} />
        </div>
        <div className="right">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/report-v3.pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="v3-download-pdf"
            onClick={async (e) => {
              // Use authenticated download via fetch + blob to attach token
              e.preventDefault();
              try {
                const res = await axiosInstance.get(`/clients/${clientId}/report-v3.pdf`, { responseType: 'blob' });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `CoinBills_${report?.cover?.client_code || clientId}_Report_v3.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                alert('Could not download PDF: ' + (err?.response?.data?.detail || err.message));
              }
            }}
          >
            Download PDF
          </a>
          <Link to="/clients" data-testid="v3-back-to-clients">All Clients</Link>
          <button onClick={() => load()} data-testid="v3-reload-btn">Regenerate</button>
        </div>
      </div>

      <Cover cover={report.cover} />
      <Snapshot snapshot={report.snapshot} />
      <CapitalJourney capital_journey={report.capital_journey} sym={sym} />
      <Performance
        performance={report.performance}
        snapshot={report.snapshot}
        calendarYearRoi={report.calendar_year_roi}
      />
      <AIInsights ai_insights={report.ai_insights} />
      <ReportFooter footer={report.footer} />
    </div>
  );
}
